import { FC, useMemo } from 'react'
import { Button, Typography } from '@mui/material'
import { Plus } from '@emotion-icons/fa-solid/Plus'
import styled from '@emotion/styled'
import { Colors } from 'src/components/theme'
import Link from 'next/link'
import { GenericLoader } from 'src/v2/components/skeleton-loaders'
import { SidebarResultOverlayContainer } from 'src/v2/components/sidebar-result-overlay-container'
import { DarkTooltip } from 'src/components/tooltip/dark-tooltip'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ItemContainer = styled.div`
  border: 1px solid ${Colors.neutral100};
  padding: 8px;
  display: flex;
  gap: 10px;
  align-items: center;
  border-radius: 6px;

  :hover {
    background-color: ${Colors.neutral50};
    cursor: pointer;
  }
`

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  img {
    width: 24px;
    height: 24px;
  }
`

const ItemTitle = styled(Typography)`
  line-height: 1;
  font-family: inherit;
`

const ItemSubtitle = styled(Typography)`
  font-family: inherit;
`

const ItemLink = styled(Link)`
  color: ${Colors.blueDark};
`

const ItemInfo = styled.div`
  flex: 1;
  font-family: inherit;
`

const ShowMoreButton = styled(Button)`
  border-color: ${Colors.primaryBlue200};
  color: ${Colors.blueDark};
  text-transform: none;
  font-size: 14px;
  font-weight: 700;
`

type SidebarResultOverlayListLoadingProps = {
  numberOfItems?: number
}
export const SidebarResultOverlayListLoading: FC<SidebarResultOverlayListLoadingProps> = ({ numberOfItems = 3 }) => {
  return (
    <Container>
      <GenericLoader color="medium" variant="rounded" width={85} height={12} animation="wave" />
      {Array.from({ length: numberOfItems }).map((_, index) => (
        <GenericLoader key={index} color="light" variant="rounded" width={360} height={40} animation="wave" />
      ))}
    </Container>
  )
}

export type SidebarResultOverlayListItem = {
  id: string
  title: string
  subtitle?: string
  iconBgColor: string
  icon: JSX.Element
  link: string
  linkIcon: JSX.Element
}

type SidebarResultOverlayListItemProps = {
  item: SidebarResultOverlayListItem
  onClick?: () => void
}
const SidebarResultOverlayListItem: FC<SidebarResultOverlayListItemProps> = ({ item, onClick }) => {
  return (
    <ItemContainer onClick={onClick}>
      <IconWrapper style={{ backgroundColor: item.iconBgColor }}>{item.icon}</IconWrapper>
      <ItemInfo>
        <ItemTitle color={Colors.neutral700} fontSize={14} fontWeight={700}>
          {item.title}
        </ItemTitle>
        {item.subtitle && (
          <ItemSubtitle color={Colors.neutral400} fontSize={12}>
            {item.subtitle}
          </ItemSubtitle>
        )}
      </ItemInfo>
      <DarkTooltip arrow={false} placement="top-start" title="Open in a new tab">
        <ItemLink
          href={item.link}
          target="_blank"
          onClick={(e) => {
            e.stopPropagation()
          }}>
          {item.linkIcon}
        </ItemLink>
      </DarkTooltip>
    </ItemContainer>
  )
}

type SidebarResultOverlayListProps = {
  title: string
  items: SidebarResultOverlayListItem[]
  defaultSize?: number
  onClickItem?: (item: SidebarResultOverlayListItem) => void | Promise<void>
  showAll?: boolean
  onClickViewMore?: () => void
}
export const SidebarResultOverlayList: React.FC<SidebarResultOverlayListProps> = ({
  title,
  items,
  defaultSize = 3,
  onClickItem,
  showAll,
  onClickViewMore,
}) => {
  const itemsToShow = useMemo(() => {
    if (showAll) {
      return items
    }

    return items.slice(0, defaultSize)
  }, [defaultSize, items, showAll])

  const diff = items.length - itemsToShow.length

  return (
    <SidebarResultOverlayContainer title={`${title} (${items.length})`}>
      {itemsToShow.map((item) => (
        <SidebarResultOverlayListItem key={item.id} item={item} onClick={() => onClickItem?.(item)} />
      ))}
      {diff > 0 && (
        <ShowMoreButton variant="outlined" startIcon={<Plus size={14} />} onClick={onClickViewMore}>
          {`View ${diff} more`}
        </ShowMoreButton>
      )}
    </SidebarResultOverlayContainer>
  )
}
